import { useState } from 'react';
import SoWarehouseScan from '../pages/SoWarehouseScan';
import PoBoxImages     from '../pages/PoBoxImages';
import WhPackedBoxImages from '../pages/WhPackedBoxImages';
import { hasFunctionAccess } from '../permissionConfig';

// ── Colour tokens ───────────────────────────────────────────────────────────
const C = {
    white:   '#ffffff',
    ink:     '#111111',
    red:     '#D0202A',
    redDeep: '#9E0F17',
    green:   '#158A45',
    gray:    '#6b6b6b',
    line:    '#e6e6e6',
    panel:   '#f7f7f7',
};

// Which sub-page is active
// null          → landing (tile selection)
// 'so'          → SoWarehouseScan
// 'box-images'  → PoBoxImages
// 'packed-box-images' → WhPackedBoxImages (search & view; gated on warehouse/packed_box_images)

export default function WarehouseHome({ user, onLogout }) {
    const [activePage, setActivePage] = useState(null);

    // ── SoWarehouseScan ───────────────────────────────────────────────────
    if (activePage === 'so') {
        return (
            <SoWarehouseScan
                user={user}
                onExit={() => setActivePage(null)} // pass this if SoWarehouseScan supports it
            />
        );
    }

    // ── PO Box Images ─────────────────────────────────────────────────────
    if (activePage === 'box-images') {
        return (
            <PoBoxImages
                user={user}
                onBack={() => setActivePage(null)}
            />
        );
    }

    if (activePage === 'packed-box-images') {
        return <WhPackedBoxImages onBack={() => setActivePage(null)} />;
    }

    // ── Landing page ──────────────────────────────────────────────────────
    const S = {
        page: {
            minHeight:   '100vh',
            background:  C.white,
            fontFamily:  '-apple-system, BlinkMacSystemFont, sans-serif',
            maxWidth:    '430px',
            margin:      '0 auto',
            color:       C.ink,
        },
        header: {
            background: C.ink,
            padding:    '20px 18px 18px',
            display:        'flex',
            justifyContent: 'space-between',
            alignItems:     'flex-start',
        },
        body: {
            padding: '20px 16px 0',
        },
        tile: {
            display:       'flex',
            alignItems:    'center',
            gap:           '16px',
            background:    C.white,
            border:        `2px solid ${C.ink}`,
            borderRadius:  '12px',
            padding:       '20px 18px',
            marginBottom:  '14px',
            cursor:        'pointer',
            transition:    'background 0.15s',
        },
        tileIcon: {
            fontSize:   '36px',
            lineHeight: 1,
            flexShrink: 0,
        },
        tileTitle: {
            fontSize:   '17px',
            fontWeight: 800,
            marginBottom: '3px',
        },
        tileDesc: {
            fontSize: '12px',
            color:    C.gray,
            lineHeight: 1.4,
        },
        tileArrow: {
            marginLeft: 'auto',
            fontSize:   '20px',
            color:      C.gray,
            flexShrink: 0,
        },
    };

    const tiles = [
        {
            key:   'so',
            icon:  '📋',
            title: 'SO Warehouse Scan',
            desc:  'Scan SKU barcodes, pack boxes, and validate dispatch quantities against Sales Orders',
            color: C.ink,
        },
        {
            key:   'box-images',
            icon:  '📦',
            title: 'Box Images',
            desc:  'Photograph packed boxes and upload against a PO number for records',
            color: C.red,
        },
        hasFunctionAccess(user, 'warehouse', 'packed_box_images') && {
            key:   'packed-box-images',
            icon:  '🔍',
            title: 'Packed Box Images',
            desc:  'Search a PO number to see all packed box photos taken for it',
            color: C.ink,
        },
    ].filter(Boolean);

    return (
        <div style={S.page}>
            {/* Header */}
            <div style={S.header}>
                <div>
                    <div style={{ fontSize:'10px', color:C.red, letterSpacing:'2px', textTransform:'uppercase', fontWeight:800, marginBottom:'4px' }}>
                        Soulflower
                    </div>
                    <div style={{ fontSize:'22px', fontWeight:800, color:'#fff', marginBottom:'4px' }}>
                        Warehouse
                    </div>
                    <div style={{ fontSize:'13px', color:'#999' }}>
                        Welcome{user?.display_name ? `, ${user.display_name}` : ''}
                    </div>
                </div>
                {onLogout && (
                    <button
                        onClick={onLogout}
                        style={{
                            padding:      '7px 14px',
                            background:   'transparent',
                            border:       '1px solid rgba(255,255,255,0.25)',
                            borderRadius: '6px',
                            color:        '#ccc',
                            fontSize:     '12px',
                            fontWeight:   600,
                            cursor:       'pointer',
                            flexShrink:   0,
                        }}
                    >
                        Sign Out
                    </button>
                )}
            </div>

            <div style={S.body}>
                {/* The "Turn on notifications" toggle used to sit here. It now
                    lives only on the landing page (pages/Landing.jsx). Note that
                    warehouse-role users can't reach /landing (App.js routes them
                    to /warehouse and redirects everything else there), so they
                    have no way to self-enable notifications — deliberate, and
                    currently moot since the single shared `warehouse` account
                    holds no push subscription. Restore this block if that
                    changes. Its wrapper div went too: it only supplied the
                    bell's spacing, so keeping it would leave a 14px gap. */}
                <div style={{ fontSize:'11px', color:C.gray, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'14px' }}>
                    Select a task
                </div>

                {tiles.map((tile) => (
                    <div
                        key={tile.key}
                        style={S.tile}
                        onClick={() => setActivePage(tile.key)}
                        onMouseDown={(e) => { e.currentTarget.style.background = C.panel; }}
                        onMouseUp={(e)   => { e.currentTarget.style.background = C.white; }}
                    >
                        <div style={{ ...S.tileIcon }}>{tile.icon}</div>
                        <div style={{ flex: 1 }}>
                            <div style={{ ...S.tileTitle, color: tile.color }}>{tile.title}</div>
                            <div style={S.tileDesc}>{tile.desc}</div>
                        </div>
                        <div style={S.tileArrow}>›</div>
                    </div>
                ))}

                {/* Divider + info */}
                <div style={{ marginTop:'28px', padding:'14px', background:C.panel, borderRadius:10, border:`1px solid ${C.line}` }}>
                    <div style={{ fontSize:'11px', color:C.gray, textAlign:'center', lineHeight:1.6 }}>
                        Use the Zebra scanner for barcode scanning.<br />
                        Keep the trigger button pressed to scan.
                    </div>
                </div>
            </div>
        </div>
    );
}